import { type IDropdownOption, MessageBarType } from '@fluentui/react';
import { type IMessageBarMessage, unflattenNestedRows } from '@h2oai/ui-kit';
import { md5 } from 'js-md5';

import {
  ActivePermission,
  FeatureSchemaDraft,
  FeatureSet,
  ListableFeatureSet,
} from './gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { FeatureSetFlow } from './gen/ai/h2o/featurestore/api/v1/FeatureSetProtoApi.pb';
import * as fm from './gen/fetch.pb';
import {
  DatetimeFormatType,
  FeatureDataTypeWithExtraTypes,
  FeatureSchemaDraftWithDepth,
  SchemaErrorMessage,
} from './models';

// fetchReq has only return the r.json(). this util is for r.text()
export function fetchTextReq(path: string, init?: fm.InitReq): Promise<string> {
  const { pathPrefix, ...req } = init || {};
  const url = pathPrefix ? `${pathPrefix}${path}` : path;
  return fetch(url, req).then((r) =>
    r.text().then((body: string) => {
      if (!r.ok) {
        throw body;
      }
      return body;
    })
  ) as Promise<string>;
}

export function getErrorMessageBarMessage(err: any, message?: string): IMessageBarMessage {
  let errorMessage =
    'An unknown error occurred. If the error persists after refreshing, please contact an administrator';
  if (err instanceof Error) {
    errorMessage = `${message}${err.message || errorMessage}`;
  } else if (err.code) {
    errorMessage = `${err.reason}`;
  } else if (err.status) {
    errorMessage = `${message}${err.detail || err.error}`;
  } else if (err.errors && err.errors.length) {
    errorMessage = err.errors.join(', ');
  }
  return {
    messageBarType: MessageBarType.error,
    message: errorMessage,
  };
}

export function hasAPIError(err: any) {
  return err instanceof Error || (err.code && err.reason) || (err.errors && err.errors.length);
}

export function getDropdownOptionsByKeyNameMap(map: Map<string | undefined, string>): IDropdownOption[] {
  const options: IDropdownOption[] = [];
  map.forEach((text, key = '') => {
    options.push({ key, text, title: text });
  });
  return options;
}

export const getDropdownOptions = (
  items: { [key: string]: any }[] = [],
  keyField = 'id',
  textField = 'name'
): IDropdownOption[] => items.map((item) => ({ key: item[keyField], text: item[textField], data: item }));

export function formatDate(date: Date, formatString = '', type?: DatetimeFormatType) {
  // Spark Datetime: https://spark.apache.org/docs/latest/sql-ref-datetime-pattern.html
  if (!formatString) {
    return date.toISOString();
  }
  const patterns: { [key: string]: any } = {
    YYYY: date.getFullYear(),
    YY: date.getFullYear().toString().substring(2),
    yyyy: date.getFullYear(),
    yy: date.getFullYear().toString().substring(2),
    MMMM: date.toLocaleString('default', { month: 'long' }),
    MMM: date.toLocaleString('default', { month: 'short' }),
    MM: (date.getMonth() + 1).toString().padStart(2, '0'),
    M: date.getMonth() + 1,
    LLLL: date.toLocaleString('default', { month: 'long' }), // Spark
    LLL: date.toLocaleString('default', { month: 'short' }), // Spark
    LL: (date.getMonth() + 1).toString().padStart(2, '0'), // Spark
    L: date.getMonth() + 1, // Spark
    DDDD: date.toLocaleDateString('default', { weekday: 'long' }),
    DDD: date.toLocaleDateString('default', { weekday: 'short' }),
    EE: date.toLocaleDateString('default', { weekday: 'long' }), // Spark
    E: date.toLocaleDateString('default', { weekday: 'short' }), // Spark
    DD: date.getDate().toString().padStart(2, '0'),
    D: date.getDate(),
    dddd: date.toLocaleDateString('default', { weekday: 'long' }),
    ddd: date.toLocaleDateString('default', { weekday: 'short' }),
    dd: date.getDate().toString().padStart(2, '0'),
    d: date.getDate(),
    HH: date.getHours().toString().padStart(2, '0'), // military 00-23
    H: date.getHours().toString(), // military 0-23
    hh: (date.getHours() % 12).toString().padStart(2, '0'), // 00-11
    h: (date.getHours() % 12).toString(), // 0-11
    KK: (date.getHours() % 12).toString().padStart(2, '0'), // Spark 00-11
    K: date.getHours().toString(), // Spark 0-11
    kk: (date.getHours() + 1).toString().padStart(2, '0'), // Spark 01-24,
    k: (date.getHours() + 1).toString(), // Spark 1-24
    mm: date.getMinutes().toString().padStart(2, '0'),
    m: date.getMinutes(),
    SS: date.getSeconds().toString().padStart(2, '0'),
    S: date.getSeconds(),
    ss: date.getSeconds().toString().padStart(2, '0'),
    s: date.getSeconds(),
    TTT: date.getMilliseconds().toString().padStart(3, '0'),
    ttt: date.getMilliseconds().toString().padStart(3, '0'),
    A: date.getHours() < 13 ? 'AM' : 'PM',
    a: date.getHours() < 13 ? 'am' : 'pm',
  };

  if (type === DatetimeFormatType.spark) {
    patterns.hh = ((date.getHours() % 12) + 1).toString().padStart(2, '0'); // 01-12
    patterns.h = ((date.getHours() % 12) + 1).toString(); // 1-12
    patterns.a = date.getHours() < 13 ? 'AM' : 'PM'; // Spark
  }

  return Object.entries(patterns).reduce((acc, entry) => {
    return acc.replace(entry[0], entry[1].toString());
  }, formatString);
}

// feature utility
export const mergeDataTypeArrayChild = (items: FeatureSchemaDraftWithDepth[]) => {
  const newItems = [];
  let totalRemovedArrayRowCount = 0;
  let firstArrayDataTypeDepth: boolean[] | undefined = undefined;
  const len = items.length;
  for (let i = 0; i < len; i++) {
    if (items[i].dataType === FeatureDataTypeWithExtraTypes.Array) {
      if (firstArrayDataTypeDepth === undefined) {
        firstArrayDataTypeDepth = items[i].depthWithHasNextSibling || [];
      }
      const parentRowIndex = i;
      let arrayCount = 1;
      i++;
      while (items[i].dataType === FeatureDataTypeWithExtraTypes.Array) {
        arrayCount++;
        i++;
      }
      totalRemovedArrayRowCount += arrayCount;
      let dataType = items[i].dataType;
      // add Array
      for (let j = 0; j < arrayCount; j++) {
        dataType = `Array<${dataType}>`;
      }
      items[parentRowIndex].dataType = dataType;
      newItems.push(items[parentRowIndex]);
    } else {
      // adjust depth since the parents are removed.
      const depth = items[i].depthWithHasNextSibling || [];
      if (firstArrayDataTypeDepth === undefined || depth.length > firstArrayDataTypeDepth.length) {
        const index = depth.length - totalRemovedArrayRowCount;
        if (index >= 0) {
          depth.splice(depth.length - totalRemovedArrayRowCount);
        } else {
          depth.splice(0);
        }
        items[i].depthWithHasNextSibling = [...depth];
      } else {
        firstArrayDataTypeDepth = undefined;
        totalRemovedArrayRowCount = 0;
      }
      newItems.push(items[i]);
    }
  }
  return newItems;
};

export const unflattenSchema = (items: FeatureSchemaDraftWithDepth[]): FeatureSchemaDraft[] => {
  items = mergeDataTypeArrayChild(items);
  const result = unflattenNestedRows(items, 'nested').map((item) => {
    const { spi, pci, rpi, demographic, sensitive, ...rest } = item;
    const itemCopy = {
      ...rest,
      specialData: {
        spi: item.spi,
        pci: item.pci,
        rpi: item.rpi,
        demographic: item.demographic,
        sensitive: item.sensitive,
      },
    };
    return itemCopy;
  });
  return result;
};

export const getSchemaErrorMessage = (features: FeatureSchemaDraftWithDepth[]) => {
  if (features.length === 0) {
    return SchemaErrorMessage.requiredFeature;
  }
  const hasError = features.some((item) => {
    const fieldMeta = item.__meta__?.fieldMeta;
    if (fieldMeta) {
      return Object.keys(fieldMeta).some((key) => !!fieldMeta[key].errorMessage);
    }
    return false;
  });
  return hasError ? SchemaErrorMessage.validationError : '';
};

export const getMd5Checksum = (e: ProgressEvent<FileReader>) => {
  if (e.target && e.target.result) {
    return md5.base64(e.target.result);
  }
  return '';
};

export const hasGreaterPermission = (
  current: ActivePermission = ActivePermission.ACTIVE_PERMISSION_NONE,
  target: ActivePermission
) => {
  const permissions = [
    ActivePermission.ACTIVE_PERMISSION_NONE,
    ActivePermission.ACTIVE_PERMISSION_VIEWER,
    ActivePermission.ACTIVE_PERMISSION_CONSUMER,
    ActivePermission.ACTIVE_PERMISSION_SENSITIVE_CONSUMER,
    ActivePermission.ACTIVE_PERMISSION_EDITOR,
    ActivePermission.ACTIVE_PERMISSION_OWNER,
  ];
  const currentIndex = permissions.indexOf(current);
  const targetIndex = permissions.indexOf(target);
  return currentIndex >= targetIndex;
};

export const convertFeatureSetToListableFeatureSet = (item: FeatureSet = {}): ListableFeatureSet => {
  const { featureSetName: name, project: projectName, flow, statistics: { recordsCount } = {}, ...listable } = item;
  return {
    ...listable,
    name,
    projectName,
    recordsCount,
    flow: flow as FeatureSetFlow,
  };
};
